import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { CheckIcon } from "@heroicons/react/24/outline";
import { loadStripe } from "@stripe/stripe-js/pure";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { SiteLight } from "~/layouts/site-light";
import { analytics } from "@lib/analytics";

interface PlanDisplay {
  name: string;
  interval: string;
  amount_cents: number;
  monthly_amount_cents: number;
  annual_amount_cents: number | null;
  trial_period_days: number;
  tier_name: string | null;
  features: string[] | null;
}

interface SubscriptionsNewProps {
  client_secret: string;
  publishable_key: string;
  plan: PlanDisplay;
  return_to?: string | null;
  return_url: string;
}

function formatCurrency(cents: number): string {
  const dollars = cents / 100;
  return `$${Math.round(dollars).toLocaleString("en-US")}`;
}

function SubscriptionsNew({
  client_secret,
  publishable_key,
  plan,
  return_url,
}: SubscriptionsNewProps) {
  const stripePromise = useMemo(() => loadStripe(publishable_key), [publishable_key]);
  const completedRef = useRef(false);
  const abandonedFiredRef = useRef(false);

  const checkoutOptions = useMemo(
    () => ({
      clientSecret: client_secret,
      onComplete: () => {
        completedRef.current = true;
        analytics.track("checkout_completed", { plan: plan.name, interval: plan.interval });
        window.location.href = return_url;
      },
    }),
    [client_secret, plan.name, plan.interval, return_url],
  );

  useEffect(() => {
    const props = { plan: plan.name, interval: plan.interval };
    analytics.track("checkout_started", props);

    const fireAbandoned = () => {
      if (completedRef.current || abandonedFiredRef.current) return;
      abandonedFiredRef.current = true;
      analytics.track("checkout_abandoned", props);
    };

    window.addEventListener("pagehide", fireAbandoned);
    return () => {
      window.removeEventListener("pagehide", fireAbandoned);
      fireAbandoned();
    };
  }, [plan.name, plan.interval]);

  const isAnnual = plan.interval === "year";
  const monthly = formatCurrency(plan.monthly_amount_cents);
  const annualTotal = plan.annual_amount_cents ? formatCurrency(plan.annual_amount_cents) : null;

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-6xl px-6 py-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <p className="text-sm uppercase tracking-wide text-[#74767a] mb-2">
            {plan.tier_name ?? "Plan"}
          </p>
          {plan.trial_period_days > 0 ? (
            <>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-semibold text-[#0f1113]">
                  {plan.trial_period_days} day free trial
                </span>
              </div>
              <p className="text-sm text-[#74767a] mb-6">
                then {monthly}/mo afterwards
                {isAnnual && annualTotal && ", billed annually"}
              </p>
              <div className="mb-6 rounded-lg bg-[#f4f4f4] px-4 py-3">
                <p className="text-sm text-[#2e3238]">
                  <strong>{plan.trial_period_days}-day free trial.</strong>{" "}
                  Cancel anytime.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-5xl font-semibold text-[#0f1113]">{monthly}</span>
                <span className="text-base text-[#74767a]">/mo</span>
              </div>
              {isAnnual && annualTotal && (
                <p className="text-sm text-[#74767a] mb-6">
                  billed annually.
                </p>
              )}
              {!isAnnual && <p className="text-sm text-[#74767a] mb-6">billed monthly</p>}
            </>
          )}

          {plan.features && plan.features.length > 0 && (
            <ul className="space-y-3">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-[#2e3238]">
                  <CheckIcon className="h-5 w-5 shrink-0 text-[#0f1113]" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={checkoutOptions}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      </div>
    </div>
  );
}

SubscriptionsNew.layout = (page: ReactNode) => <SiteLight>{page}</SiteLight>;

export default SubscriptionsNew;
